/**
 * SenderAccountIdentity (KAN-618, KAN-636)
 *
 * OUR Telegram account that talks to the client: avatar (picture or initials),
 * profile name and @username. Shared by the "Наш аккаунт" cell of the clients
 * list and the header of the dialog modal. Renders `fallback` when there is no
 * account or nothing to show about it.
 */
import type { ReactNode } from 'react';
import { SenderAvatar } from '@/components/messages/SenderAvatar';
import {
  senderAccountName,
  senderAccountUsername,
  type SenderAccountDisplay,
} from '@/lib/senderAccount';
import { cn } from '@/lib/utils';

interface SenderAccountIdentityProps {
  account: SenderAccountDisplay | null | undefined;
  /** data-testid of the rendered element (also used for the fallback). */
  testId: string;
  /** Rendered instead when there is no account; nothing by default. */
  fallback?: ReactNode;
  className?: string;
}

export function SenderAccountIdentity({
  account,
  testId,
  fallback = null,
  className,
}: SenderAccountIdentityProps): JSX.Element {
  const name = account ? senderAccountName(account) : '';
  const username = account ? senderAccountUsername(account.username) : '';

  if (!account || (!name && !username)) {
    return <>{fallback}</>;
  }

  return (
    <span
      data-testid={testId}
      className={cn('inline-flex min-w-0 max-w-full items-center gap-2', className)}
    >
      <SenderAvatar
        avatarUrl={account.avatar_url}
        name={name || username}
        size={24}
      />
      <span className="flex min-w-0 flex-col leading-tight">
        {name && <span className="truncate text-sm text-gray-900">{name}</span>}
        {username && (
          <span className="truncate text-xs text-gray-400">@{username}</span>
        )}
      </span>
    </span>
  );
}
